import React, { useState, useEffect, useRef } from 'react';
import { logger } from '@/utils/logger';
import { createPortal } from 'react-dom';
import { useProject } from '@/contexts/ProjectContext';
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useArchiveProject,
} from '@/hooks/useProjects';
import { EmptyState } from '@/components/shared/EmptyState';
import { getCeloxisProjects, searchCeloxisProjects, checkCeloxisConnection } from '@/api/celoxis';
import type { Project } from '@fmeastudio/shared';
import type { CeloxisProject } from '@/api/celoxis';
import './ProjectSwitcher.css';

export const ProjectSwitcher: React.FC = () => {
  const { activeProject, setActiveProject } = useProject();
  const [showArchived, setShowArchived] = useState(() => {
    return localStorage.getItem('showArchivedProjects') === 'true';
  });
  const { data: projects = [], isLoading } = useProjects(showArchived);
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const archiveProject = useArchiveProject();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');

  // Action menu state
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Edit modal state
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Celoxis integration state
  const [editCeloxisProjectId, setEditCeloxisProjectId] = useState<string | null>(null);
  const [editCeloxisProjectNumber, setEditCeloxisProjectNumber] = useState<string | null>(null);
  const [editCeloxisProjectName, setEditCeloxisProjectName] = useState<string | null>(null);
  const [celoxisProjects, setCeloxisProjects] = useState<CeloxisProject[]>([]);
  const [celoxisSearch, setCeloxisSearch] = useState('');
  const [celoxisConnected, setCeloxisConnected] = useState<boolean | null>(null);
  const [loadingCeloxis, setLoadingCeloxis] = useState(false);

  // Delete modal state
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeMenu]);

  // Close dropdown when clicking outside (for portal)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsCreating(false);
        setNewProjectName('');
        setNewProjectDescription('');
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSwitch = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      setActiveProject(project);
      setIsOpen(false);
      setActiveMenu(null);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newProjectName.trim()) {
      return;
    }

    try {
      const newProject = await createProject.mutateAsync({
        name: newProjectName,
        description: newProjectDescription || undefined,
      });

      setActiveProject(newProject);
      setNewProjectName('');
      setNewProjectDescription('');
      setIsCreating(false);
      setIsOpen(false);
    } catch (error) {
      logger.error('Failed to create project', error, { module: 'ProjectSwitcher' });
    }
  };

  const handleCancel = () => {
    setNewProjectName('');
    setNewProjectDescription('');
    setIsCreating(false);
  };

  const handleToggleArchived = (checked: boolean) => {
    setShowArchived(checked);
    localStorage.setItem('showArchivedProjects', checked.toString());
  };

  const handleMenuToggle = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    setActiveMenu(activeMenu === projectId ? null : projectId);
  };

  const handleEdit = async (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setEditingProject(project);
    setEditName(project.name);
    setEditDescription(project.description || '');
    setEditCeloxisProjectId(project.celoxisProjectId || null);
    setEditCeloxisProjectNumber(project.celoxisProjectNumber || null);
    setEditCeloxisProjectName(project.celoxisProjectName || null);
    setCeloxisSearch('');
    setActiveMenu(null);

    // Check Celoxis connection and fetch projects
    setLoadingCeloxis(true);
    try {
      const status = await checkCeloxisConnection();
      setCeloxisConnected(status.connected);
      if (status.connected) {
        const projects = await getCeloxisProjects();
        setCeloxisProjects(projects);
      }
    } catch {
      setCeloxisConnected(false);
    } finally {
      setLoadingCeloxis(false);
    }
  };

  // Handle Celoxis project search
  const handleCeloxisSearch = async (query: string) => {
    setCeloxisSearch(query);
    if (!query.trim()) {
      // Fetch all projects when search is cleared
      try {
        const projects = await getCeloxisProjects();
        setCeloxisProjects(projects);
      } catch {
        // Ignore search errors
      }
      return;
    }
    try {
      const results = await searchCeloxisProjects(query);
      setCeloxisProjects(results);
    } catch {
      // Ignore search errors
    }
  };

  // Handle selecting a Celoxis project
  const handleSelectCeloxisProject = (project: CeloxisProject | null) => {
    if (project) {
      setEditCeloxisProjectId(project.id);
      setEditCeloxisProjectNumber(project.code);
      setEditCeloxisProjectName(project.name);
    } else {
      setEditCeloxisProjectId(null);
      setEditCeloxisProjectNumber(null);
      setEditCeloxisProjectName(null);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingProject || !editName.trim()) {
      return;
    }

    try {
      await updateProject.mutateAsync({
        id: editingProject.id,
        data: {
          name: editName,
          description: editDescription || null,
          celoxisProjectId: editCeloxisProjectId,
          celoxisProjectNumber: editCeloxisProjectNumber,
          celoxisProjectName: editCeloxisProjectName,
        },
      });

      // Update active project if editing the active one
      if (activeProject?.id === editingProject.id) {
        setActiveProject({
          ...editingProject,
          name: editName,
          description: editDescription || null,
          celoxisProjectId: editCeloxisProjectId,
          celoxisProjectNumber: editCeloxisProjectNumber,
          celoxisProjectName: editCeloxisProjectName,
        });
      }

      setEditingProject(null);
      setEditName('');
      setEditDescription('');
      setEditCeloxisProjectId(null);
      setEditCeloxisProjectNumber(null);
      setEditCeloxisProjectName(null);
      setCeloxisProjects([]);
    } catch (error) {
      logger.error('Failed to update project', error, { module: 'ProjectSwitcher' });
    }
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditName('');
    setEditDescription('');
    setEditCeloxisProjectId(null);
    setEditCeloxisProjectNumber(null);
    setEditCeloxisProjectName(null);
    setCeloxisProjects([]);
  };

  const handleArchive = async (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setActiveMenu(null);

    try {
      await archiveProject.mutateAsync(project.id);

      // If archiving active project, switch to another
      if (activeProject?.id === project.id) {
        const remainingProjects = projects.filter(
          (p) => p.id !== project.id && p.status === 'ACTIVE'
        );
        if (remainingProjects.length > 0) {
          setActiveProject(remainingProjects[0]);
        } else {
          setActiveProject(null);
        }
      }
    } catch (error) {
      logger.error('Failed to archive project', error, { module: 'ProjectSwitcher' });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setDeletingProject(project);
    setActiveMenu(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProject) return;

    try {
      await deleteProject.mutateAsync(deletingProject.id);

      // If deleting active project, switch to another
      if (activeProject?.id === deletingProject.id) {
        const remainingProjects = projects.filter(
          (p) => p.id !== deletingProject.id && !p.deletedAt
        );
        if (remainingProjects.length > 0) {
          setActiveProject(remainingProjects[0]);
        } else {
          setActiveProject(null);
        }
      }

      setDeletingProject(null);
    } catch (error) {
      logger.error('Failed to delete project', error, { module: 'ProjectSwitcher' });
    }
  };

  const handleCancelDelete = () => {
    setDeletingProject(null);
  };

  const handleConvertToTemplate = async (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setActiveMenu(null);

    if (
      !confirm(
        `Convert "${project.name}" to a template? This will change its status and it will appear in the template library.`
      )
    ) {
      return;
    }

    try {
      await updateProject.mutateAsync({
        id: project.id,
        data: {
          status: 'TEMPLATE',
        },
      });

      // If converting active project, switch to another
      if (activeProject?.id === project.id) {
        const remainingProjects = projects.filter(
          (p) => p.id !== project.id && p.status === 'ACTIVE'
        );
        if (remainingProjects.length > 0) {
          setActiveProject(remainingProjects[0]);
        } else {
          setActiveProject(null);
        }
      }
    } catch (error) {
      logger.error('Failed to convert project to template', error, { module: 'ProjectSwitcher' });
    }
  };

  if (isLoading) {
    return (
      <div className="project-switcher">
        <button className="project-switcher-button" disabled>
          Loading...
        </button>
      </div>
    );
  }

  const handleToggle = () => {
    if (isOpen) {
      // Closing dropdown - reset form state
      setIsCreating(false);
      setNewProjectName('');
      setNewProjectDescription('');
      setActiveMenu(null);
    } else {
      // Opening dropdown - calculate position
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="project-switcher" data-testid="project-switcher">
      <button
        ref={buttonRef}
        className="project-switcher-button"
        onClick={handleToggle}
        data-testid="project-switcher-button"
      >
        <span className="project-icon">📁</span>
        <span className="project-name">{activeProject?.name || 'No Project Selected'}</span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="project-dropdown project-dropdown--portal"
          data-testid="project-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          <div className="project-dropdown-header">
            <span>Switch Project</span>
            <label className="archive-toggle" data-testid="archive-toggle-label">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => handleToggleArchived(e.target.checked)}
                data-testid="show-archived-toggle"
              />
              <span className="archive-toggle-text">Show Archived</span>
            </label>
          </div>

          <div className="project-list" data-testid="project-list">
            {projects.length === 0 ? (
              !isCreating ? (
                <div className="project-empty-state">
                  <EmptyState
                    icon={
                      <span className="empty-icon" style={{ fontSize: '48px' }}>
                        📁
                      </span>
                    }
                    title="No projects yet"
                    description="Projects help you organize your FMEA work. Create your first project to get started analyzing failure modes and effects."
                    action={{
                      label: 'Create First Project',
                      onClick: () => setIsCreating(true),
                      testId: 'create-first-project-btn',
                    }}
                  />
                  <a
                    href="/docs/getting-started"
                    className="empty-state-docs-link"
                    data-testid="empty-state-docs-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📚 Learn more about projects
                  </a>
                </div>
              ) : null
            ) : (
              projects.map((project) => (
                <div
                  key={project.id}
                  className={`project-item-wrapper ${
                    activeProject?.id === project.id ? 'active' : ''
                  }`}
                >
                  <div
                    className="project-item"
                    onClick={() => handleSwitch(project.id)}
                    data-testid="project-item"
                    data-project-id={project.id}
                    data-project-name={project.name}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSwitch(project.id);
                      }
                    }}
                  >
                    <div className="project-item-content">
                      <div className="project-item-info">
                        <div className="project-item-name">
                          {project.name}
                          {project.status === 'ARCHIVED' && (
                            <span className="archived-badge">Archived</span>
                          )}
                        </div>
                        {project.description && (
                          <div className="project-item-description">{project.description}</div>
                        )}
                      </div>
                      <button
                        className="project-actions-trigger"
                        onClick={(e) => handleMenuToggle(e, project.id)}
                        data-testid="project-actions-menu"
                        aria-label="Project actions"
                      >
                        ⋮
                      </button>
                    </div>
                  </div>

                  {activeMenu === project.id && (
                    <div
                      ref={menuRef}
                      className="project-actions-dropdown"
                      data-testid="project-actions-dropdown"
                    >
                      <button
                        className="project-action-item"
                        onClick={(e) => handleEdit(e, project)}
                        data-testid="edit-project-btn"
                      >
                        Edit
                      </button>
                      <button
                        className="project-action-item"
                        onClick={(e) => handleConvertToTemplate(e, project)}
                        data-testid="save-as-template-btn"
                      >
                        Save as Template
                      </button>
                      <button
                        className="project-action-item"
                        onClick={(e) => handleArchive(e, project)}
                        data-testid="archive-project-btn"
                      >
                        Archive
                      </button>
                      <button
                        className="project-action-item project-action-delete"
                        onClick={(e) => handleDeleteClick(e, project)}
                        data-testid="delete-project-btn"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="project-dropdown-footer">
            {!isCreating ? (
              <button
                className="project-create-button"
                onClick={() => setIsCreating(true)}
                data-testid="create-project-btn"
              >
                <span>+</span> New Project
              </button>
            ) : (
              <form className="project-create-form" onSubmit={handleCreate}>
                <input
                  type="text"
                  placeholder="Project Name *"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  autoFocus
                  required
                  data-testid="project-name-input"
                />
                <textarea
                  placeholder="Description (optional)"
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  rows={2}
                  data-testid="project-description-input"
                />
                <div className="project-create-actions">
                  <button
                    type="button"
                    className="project-cancel-button"
                    onClick={handleCancel}
                    data-testid="cancel-project-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="project-submit-button"
                    disabled={!newProjectName.trim() || createProject.isPending}
                    data-testid="submit-project-btn"
                  >
                    {createProject.isPending ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <div className="modal-overlay" data-testid="edit-project-modal">
          <div className="modal-container">
            <h3 className="modal-title">Edit Project</h3>
            <form onSubmit={handleSaveEdit}>
              <div className="modal-field">
                <label htmlFor="edit-name">Name *</label>
                <input
                  id="edit-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  maxLength={100}
                  data-testid="edit-project-name"
                />
              </div>
              <div className="modal-field">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                  data-testid="edit-project-description"
                />
              </div>

              {/* Celoxis Integration Section */}
              <div className="modal-section">
                <h4 className="modal-section-title">Celoxis Integration</h4>

                {loadingCeloxis && (
                  <div className="celoxis-loading">Checking Celoxis connection...</div>
                )}

                {!loadingCeloxis && celoxisConnected === false && (
                  <div className="celoxis-error">
                    Unable to connect to Celoxis API. Check your API token configuration.
                  </div>
                )}

                {!loadingCeloxis && celoxisConnected === true && (
                  <>
                    {/* Current Mapping Display */}
                    {editCeloxisProjectId && (
                      <div className="celoxis-current">
                        <span className="celoxis-label">Linked to:</span>
                        <span className="celoxis-value">
                          {editCeloxisProjectName || 'Unknown'}
                          {editCeloxisProjectNumber && ` (${editCeloxisProjectNumber})`}
                        </span>
                        <button
                          type="button"
                          className="celoxis-unlink"
                          onClick={() => handleSelectCeloxisProject(null)}
                          data-testid="unlink-celoxis-btn"
                        >
                          Unlink
                        </button>
                      </div>
                    )}

                    {/* Project Search/Select */}
                    <div className="modal-field">
                      <label htmlFor="celoxis-search">
                        {editCeloxisProjectId ? 'Change Celoxis Project' : 'Link to Celoxis Project'}
                      </label>
                      <input
                        id="celoxis-search"
                        type="text"
                        placeholder="Search by name or project number (e.g., PRO-271)"
                        value={celoxisSearch}
                        onChange={(e) => handleCeloxisSearch(e.target.value)}
                        data-testid="celoxis-search-input"
                      />
                    </div>

                    {/* Celoxis Projects List */}
                    {celoxisProjects.length > 0 && (
                      <div className="celoxis-project-list" data-testid="celoxis-project-list">
                        {celoxisProjects.map((project) => (
                          <div
                            key={project.id}
                            className={`celoxis-project-item ${editCeloxisProjectId === project.id ? 'selected' : ''}`}
                            onClick={() => handleSelectCeloxisProject(project)}
                            data-testid="celoxis-project-item"
                          >
                            <span className="celoxis-project-name">{project.name}</span>
                            {project.code && (
                              <span className="celoxis-project-code">{project.code}</span>
                            )}
                            {project.clientName && (
                              <span className="celoxis-project-client">{project.clientName}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {!celoxisSearch && celoxisProjects.length === 0 && !editCeloxisProjectId && (
                      <div className="celoxis-hint">
                        No Celoxis projects loaded. Start typing to search.
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-button"
                  onClick={handleCancelEdit}
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-submit-button"
                  disabled={!editName.trim() || updateProject.isPending}
                  data-testid="save-project-btn"
                >
                  {updateProject.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingProject && (
        <div className="modal-overlay" data-testid="delete-project-modal">
          <div className="modal-container">
            <h3 className="modal-title">Delete Project</h3>
            <p className="modal-message">
              Are you sure you want to delete <strong>"{deletingProject.name}"</strong>?
            </p>
            <p className="modal-warning">
              This action can be undone (soft delete). The project will be marked as deleted but
              data will be preserved.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-cancel-button"
                onClick={handleCancelDelete}
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-delete-button"
                onClick={handleConfirmDelete}
                disabled={deleteProject.isPending}
                data-testid="confirm-delete-btn"
              >
                {deleteProject.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
